import QuestionMediaRenderer from "./QuestionMediaRenderer";

type Props = {
  setShowCorrectAnswer: (value: boolean) => void;
  setIsSecondChanceActive: (value: boolean) => void;
  questionData: any;
  currentLanguage: string;
  setOptionImagePreviewUrl: (url: string | null) => void;
  getLocalizedField: (value: unknown, lang: string) => string;
  t: any;
  isSkipLifelineAnswerReveal: boolean;
  gameData: any;
  handleAnswerClick: (answer: string, teamId: string) => void;
  handleNoneAnswered: () => void;
  onNavigateNextQuestion: () => void;
  /** Overrides default `gameAnswer.whoMadeTheCorrectAnswer` (e.g. 1000-question screen). */
  whoMadeTheCorrectAnswerText?: string;
  /** Overrides default `gameAnswer.noOne` (e.g. 1000-question screen). */
  noneAnsweredText?: string;
};

const GameAnswerCorrectAnswerContent = ({
  setShowCorrectAnswer,
  setIsSecondChanceActive,
  questionData,
  currentLanguage,
  setOptionImagePreviewUrl,
  getLocalizedField,
  t,
  isSkipLifelineAnswerReveal,
  gameData,
  handleAnswerClick,
  handleNoneAnswered,
  onNavigateNextQuestion,
  whoMadeTheCorrectAnswerText,
  noneAnsweredText,
}: Props) => {
  return (
    <div className="w-full flex-1 2xl:rounded-[1vw] xl:rounded-[1.04vw] rounded-2xl 2xl:border-[0.2vw] xl:border-3 border-2 border-brown-primary bg-brown-primary 2xl:p-[0.03vw] xl:p-0.5 p-2 flex 2xl:gap-[1vw] xl:gap-4 md:gap-3 gap-2 overflow-hidden sm:flex-row flex-col">
      <div className="bg-white 2xl:border-[0.208vw] xl:border-4 border-2 border-brown-primary w-full 2xl:max-w-[calc(100vw-20.625vw)] xl:max-w-[calc(100vw-396px)] lg:max-w-[calc(100vw-288px)] md:max-w-[calc(100vw-256px)] max-w-full 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1vw] p-3 landscape:max-lg:p-2 overflow-hidden sm:h-full flex-1">
        <div className="relative w-full mx-auto flex flex-col items-center justify-center 2xl:min-h-[calc(100%-2.126vw)] xl:min-h-[calc(100%-60px)] sm:min-h-[calc(100%-32px)] h-full">
          {questionData?.answer &&
            (questionData?.option_type === "image" ? (
              <div className="flex justify-center items-center h-full py-2">
                <button
                  type="button"
                  onClick={() => {
                    const url = getLocalizedField(questionData.answer, currentLanguage);
                    if (url) setOptionImagePreviewUrl(url);
                  }}
                  className="shrink-0 w-[100px] h-[100px] rounded-lg border-[1.5px] border-black-300 bg-white overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-95 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                  aria-label={t("gameAnswer.previewCorrectAnswerImage") || "Preview correct answer"}
                >
                  <img
                    src={getLocalizedField(questionData.answer, currentLanguage)}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </button>
              </div>
            ) : (
              <div className={`flex justify-center ${questionData?.media_answer ? "items-end" : "items-center"}  flex-1 pt-6`}>
                <h2 dir="auto" className="2xl:text-[2.5625vw]/[2.75vw] xl:text-4xl/[2.6rem] md:text-3xl/[2.1rem] text-2xl/[1.75rem] font-garamond font-bold text-center whitespace-pre-line wrap-break-word">
                  {questionData?.answer?.[currentLanguage]}
                </h2>
              </div>
            ))}
          {questionData?.media_answer && (
            <div className="w-full mx-auto flex flex-col items-center justify-center h-[75%]">
              <QuestionMediaRenderer
                media={questionData?.media_answer}
                mediaType={questionData?.answer_type}
                showPreviewTimer
                previewTimerInitialTime={20}
              />
              {!["image", "video", "audio"].includes(questionData?.answer_type) &&
                questionData?.answer?.[currentLanguage] && (
                  <p dir="auto" className="text-[#7364FF] font-bold 2xl:text-[3.333vw]/tight xl:text-5xl/tight text-4xl/9 flex-1 h-full flex items-center justify-center sm:my-0 my-6 whitespace-pre-line wrap-break-word">
                    {questionData?.answer[currentLanguage]}
                  </p>
                )}
            </div>
          )
          }
          <button
            onClick={() => {
              setShowCorrectAnswer(false);
              setIsSecondChanceActive(false);
            }}
            className="flex items-center w-full 2xl:gap-[0.417vw] gap-2 cursor-pointer 2xl:mt-[0.7vw] md:mt-3 mt-2 landscape:max-lg:mt-2 2xl:mb-[1.5vw] xl:mb-[1.5vw] mb-2"
          >
            <svg
              className="2xl:w-[1.042vw] lg:w-5 2xl:h-[1.042vw] lg:h-5 w-4 h-4 rtl:-scale-x-100"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.7291 5.74392V13.532C19.7291 14.3452 19.0662 15.0065 18.2533 15.0065H10.6629C10.286 15.0065 9.98042 15.3131 9.98042 15.6898V18.2286C9.98042 18.6727 9.73179 19.0377 9.31756 19.1986C8.90398 19.3594 8.47357 19.2593 8.17329 18.9326L0.276258 10.3409C-0.092086 9.94012 -0.092086 9.33351 0.276258 8.93278L8.17329 0.341062C8.37026 0.126797 8.63346 0 8.92559 0C9.06007 0 9.1924 0.0286874 9.31751 0.0772499C9.7325 0.238219 9.98037 0.602437 9.98037 1.04733V3.58387C9.98037 3.96056 10.286 4.26722 10.6629 4.26722H18.2532C19.0675 4.26722 19.7291 4.92984 19.7291 5.74392Z"
                fill="#413938"
              />
            </svg>
            <span className="font-garamond font-bold 2xl:text-[1.5vw] xl:text-2xl text-lg leading-none text-center flex items-center mt-1">
              {t("gameAnswer.backToTheQuestion")}
            </span>
          </button>
        </div>
      </div>
      <div className="w-full 2xl:max-w-[15.625vw] xl:max-w-[300px] lg:max-w-52 md:max-w-44 sm:max-w-40 max-w-full grid sm:grid-cols-1 grid-cols-2 place-content-center 2xl:gap-[0.625vw] gap-2 landscape:max-lg:gap-1.5 justify-center sm:h-full h-fit pe-3">
        {isSkipLifelineAnswerReveal ? (
          <>
            {/* <p className="col-span-2 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight landscape:max-lg:text-base/tight text-black-300 font-bold text-center sm:mb-0 -mb-1">
              {t("gameAnswer.answerRevealed", { defaultValue: "Answer revealed" })}
            </p> */}
            <button
              onClick={onNavigateNextQuestion}
              className="col-span-2 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1 lg:py-0.5 sm:py-1 py-0.5 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="skew-x-10 2xl:mt-[0.208vw] mt-1">
                {t("gameAnswer.nextQuestion", { defaultValue: "Next Question" })}
              </span>
            </button>
          </>
        ) : (
          <>
            <p className="col-span-2 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight landscape:max-lg:text-base/tight text-black-300 font-bold text-center sm:mb-0 -mb-1">
              {whoMadeTheCorrectAnswerText ?? t("gameAnswer.whoMadeTheCorrectAnswer")}
            </p>
            {gameData?.teams?.map((team: any) => (
              <button
                onClick={() => handleAnswerClick(team.team_name, team._id)}
                key={team.team_name}
                className="sm:col-span-2 landscape:max-lg:col-span-2 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1 lg:py-0.5 sm:py-1 py-0.5 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
              >
                <span className="skew-x-10 2xl:mt-[0.208vw] mt-1">{team.team_name}</span>
              </button>
            ))}
            <button
              onClick={handleNoneAnswered}
              className="sm:col-span-2 landscape:max-lg:col-span-2 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1 lg:py-0.5 sm:py-1 py-0.5 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="skew-x-10 2xl:mt-[0.208vw] mt-1">{noneAnsweredText ?? t("gameAnswer.noOne")}</span>
            </button>
            <div className="flex justify-center  2xl:gap-[0.625vw] md:gap-3 gap-2 w-full mx-auto col-span-2" />
          </>
        )}
      </div>
    </div>
  );
};

export default GameAnswerCorrectAnswerContent;
